import { faStore } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../hooks/useEndpointGuard';
import usePageTitle from '../../../hooks/usePageTitle';
import useRouteDialog from '../../../hooks/useRouteDialog';
import useTableColumns from '../../../hooks/useTableColumns';
import useTableState from '../../../hooks/useTableState';
import {
  FranchiseCollection,
  FranchiseType,
} from '../../../types/api/franchises';
import { EntityIdRouteParams } from '../../../types/routing';
import { Unpacked } from '../../../types/util';
import * as franchisesGuards from '../../../utils/constants/auth/franchises';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../utils/constants/routePaths';
import { queryString } from '../../../utils/helpers/http';
import { httpQueryObject } from '../../../utils/helpers/misc';
import Table from '../../DataTable/Table/Table';
import Flex from '../../layout/flex/Flex';
import MainContent from '../../layout/flex/MainContent';
import Filters from '../Components/Filters/Filters';
import HeaderPages from '../Components/HeaderPages/HeaderPages';
import CreateEditDialog from './Dialogs/CreateEditDialog/CreateEditDialog';
import DeleteDialog from './Dialogs/Delete/DeleteDialog';
import ViewDialog from './Dialogs/ViewDialog/ViewDialog';
import {
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './Franchises.functions';
import useTableFilters from './useTableFilters';

function Franchises() {
  const { t } = useTranslation();
  const { id } = useParams<EntityIdRouteParams>();
  const [action, setAction] = useState<string>('');
  usePageTitle(t('Franchises'));

  const readGuard = useEndpointGuard(franchisesGuards.routeGuard);
  const createGuard = useEndpointGuard(franchisesGuards.addFranchise);
  const editGuard = useEndpointGuard(franchisesGuards.editFranchise);
  const deleteGuard = useEndpointGuard(franchisesGuards.deleteFranchise);

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selection,
    setSortField,
    setSortOrder,
    setSelection,
    contextMenuSelection,
    setContextMenuSelection,
  } = useTableState<Unpacked<FranchiseCollection>>(tableStorageKey);

  const { headerFiltersCount, filters, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage!, limit);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const { data, isLoading, reload, error } = useAxiosHook<FranchiseCollection>(
    '/v2/franchises' + queryString(httpQueryObject(httpFiltersObj))
  );

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'employees',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction,
          readGuard,
          editGuard,
          deleteGuard
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction,
          readGuard,
          editGuard,
          deleteGuard
        )}
      />,
    ],
    [columns, t, setContextMenuSelection, readGuard, editGuard, deleteGuard]
  );

  const {
    show: showViewDialog,
    hide: hideViewDialog,
    isVisible: isViewDialogVisible,
  } = useRouteDialog(
    RoutePaths.Franchises,
    RoutePaths.ViewFranchise,
    readGuard
  );

  const {
    show: showAddDialog,
    hide: hideAddDialog,
    isVisible: isAddDialogVisible,
  } = useRouteDialog(
    RoutePaths.Franchises,
    RoutePaths.AddFranchise,
    createGuard
  );

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    RoutePaths.Franchises,
    constructIdRoute(RoutePaths.EditFranchise, id ?? contextMenuSelection?.id),
    editGuard
  );

  const {
    show: showDeleteDialog,
    hide: hideDeleteDialog,
    isVisible: isDeleteDialogVisible,
  } = useRouteDialog(
    RoutePaths.Franchises,
    constructIdRoute(
      RoutePaths.DeleteFranchise,
      id ?? contextMenuSelection?.id
    ),
    deleteGuard
  );

  const { data: franchiseData, isLoading: isFranchiseDataLoading } =
    useAxiosHook<FranchiseType>(
      {
        url: `/v2/franchises/${id ?? contextMenuSelection?.id}`,
      },
      {
        skipWhen:
          (!id && !contextMenuSelection?.id) ||
          (!isAddDialogVisible && !isEditDialogVisible && !isViewDialogVisible),
      }
    );

  function handleCreateEditHide() {
    hideEditDialog();
    hideAddDialog();
    hideViewDialog();
  }

  function handleFromViewToEdit() {
    hideViewDialog();
    hideAddDialog();
    showEditDialog();
  }

  useEffect(() => {
    if (action && contextMenuSelection) {
      if (action === 'view-details') {
        showViewDialog();
      }
      if (action === 'edit') {
        showEditDialog();
      }
      if (action === 'delete') {
        showDeleteDialog();
      }

      setAction('');
    }
  }, [
    action,
    contextMenuSelection,
    setAction,
    showViewDialog,
    showEditDialog,
    showDeleteDialog,
  ]);

  return (
    <div className="page franchises-page">
      <HeaderPages
        title={t('Franchises')}
        subtitle={t('View and manage franchises')}
        icon={faStore}
      >
        {createGuard && (
          <Button
            type="button"
            label={t('Add new franchise')}
            icon="fas fa-plus"
            className="main-btn"
            onClick={showAddDialog}
          />
        )}
      </HeaderPages>

      <ViewDialog
        visible={isViewDialogVisible}
        onHide={handleCreateEditHide}
        data={franchiseData}
        isLoading={isFranchiseDataLoading}
        {...(readGuard ? { onEdit: handleFromViewToEdit } : {})}
      />

      <CreateEditDialog
        visible={isAddDialogVisible || isEditDialogVisible}
        isLoading={isFranchiseDataLoading}
        isEditDialog={isEditDialogVisible}
        data={franchiseData}
        onHide={handleCreateEditHide}
        reloadFranchises={reload}
      />

      <DeleteDialog
        visible={isDeleteDialogVisible}
        onHide={() => hideDeleteDialog()}
        selection={contextMenuSelection}
        reloadHubLists={reload}
      />

      <Flex direction="column">
        <Filters
          filters={filters}
          resetAllFilters={resetAllFilters}
          headerFiltersCount={headerFiltersCount}
          filterHeight={180}
        />
        <MainContent>
          <Table
            tableRef={tableRef}
            columns={finalColumns}
            data={data}
            isLoading={isLoading}
            hasError={!!error}
            reload={reload}
            headerTitle=""
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            filterHeight={180}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelection}
            sortOrder={sortOrder}
            selection={selection}
            storageString={tableStorageKey}
            rebuildTooltip
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
            exportToCSVButton
            onExportToCSVButtonClick={() =>
              (tableRef?.current as any)?.exportCSV()
            }
            contextMenuSelection={contextMenuSelection}
            setContextMenuSelection={setContextMenuSelection}
          />
        </MainContent>
      </Flex>
    </div>
  );
}
export default Franchises;
